﻿// Import of creatApp from Vue 3 bundler and axius for API requests.
import { createApp } from 'vue/dist/vue.esm-bundler';
import { setCookie, getCookie, deleteCookie, debounce } from '../functions';
const axios = require('axios');

document.addEventListener('DOMContentLoaded', () => {
    // Only build the Vue instance if the targeted element is present.
    if (document.getElementById('list-view')) {
        const listViewApp = createApp({
            // The data object returns data-attribute values instantiated in HTML and general properties for loading state, 
            // the data object with facets and resulting items, the view toggler for results, the current 'page' and textual search input binding.
            data() {
                return {
                    dataApi: '',
                    dataLimit: 10, // Default limit in BE
                    dataSortKey: '',
                    dataSortDirection: '',
                    dataAmountsSentence: '',
                    parentId: '',
                    loading: true,
                    data: null,
                    blockView: true,
                    page: 1,

                    searchText: '',
                }
            },
            watch: {
                blockView(newView, oldView) {
                    if (!newView) {
                        setCookie('ListBlockView', 'false', 365);
                    } else {
                        deleteCookie('ListBlockView');
                    }
                }
            },
            // The computed object holds checks for facets set, filters chosen and filters active.
            // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
            computed: {
                facetsSet() {
                    return this.data && this.data.facets && this.data.facets.length;
                },
                filtersChosen() {
                    return this.facetsSet && this.data.facets.some(facet => facet.values.length);
                },
                filtersActive() {
                    return this.facetsSet && (this.data.facets.some(facet => facet.selected) || this.searchText);
                },
                orderItems() {
                    if (!this.dataSortKey && !this.dataSortDirection) return [];
                    return [
                        {
                            Key: this.dataSortKey,
                            Direction: this.dataSortDirection,
                        }
                    ]
                },
                filterItems() {
                    let parentID = this.parentId;
                    if (!this.filtersChosen) return [{ Key: 'parentId.keyword', Values: [parentID], Operator: 'In' }];
                    return this.data.facets.map(facet => {
                        if (facet.values.length) {
                            return { Key: 'parentId.keyword', Values: [parentID], Operator: 'In' },{
                                Key: facet.alias,
                                Values: facet.values,
                                Operator: 4, // corresponds with 'In'
                            }
                        }
                    });
                },
                queryModel() {
                    return {
                        Limit: this.dataLimit,
                        Offset: (this.page - 1) * this.dataLimit,
                        OrderItems: this.orderItems,
                        FilterItems: this.filterItems,
                        Text: this.searchText
                    }
                },
                amountsSentence() {
                    return this.dataAmountsSentence.replace('{{queryCount}}', this.data.queryCount).replace('{{totalCount}}', this.data.totalCount);
                }
            },
            methods: {
                // The fetchData method fetches and handles API data, mainly facets and resulting items. 
                // The parameters ensure the method can be called in different ways.
                fetchData(initial = false, filterParams = null, pageParam = 0, stateUpdate = true) {
                    if (initial) this.page = 1;
                    axios
                        .post(this.dataApi, this.queryModel)
                        .then(response => {
                            if (response.status === 200) {
                                // Check if the data object should be overwritten.
                                if (initial) {
                                    this.data = response.data;
                                    // Add values array to facet items, for easier access of set filters.
                                    this.data.facets = response.data.facets.map(facet => {
                                        return {
                                            ...facet,
                                            values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
                                        }
                                    });

                                    // Check if preset filters were given in the URL or the initial load should yield more than 1 data chunk.
                                    if (filterParams) {
                                        return { action: 'PREFILTER', payload: filterParams };
                                    }
                                    else if (pageParam && pageParam > 1) {
                                        return { action: 'LOADMORE', payload: pageParam };
                                    }
                                }
                                // If no overwrite, add items to the existing items array. This is for lazy loading purposes.
                                else {
                                    response.data.items.forEach(item => this.data.items.push(item));
                                }

                                // Push state to browser URL.
                                if (stateUpdate) this.updateUrl();
                            }
                        })
                        .then(resolve => {
                            // Handle preset filters and/or loading more data chunks in initial page load.
                            if (resolve && resolve.action) {
                                switch (resolve.action) {
                                    case 'PREFILTER':
                                        this.data.facets.forEach(facet => {
                                            if (resolve.payload.has(facet.alias)) {
                                                facet.values = resolve.payload.get(facet.alias).split(',');
                                            }
                                        });
                                        this.fetchData(true, null, pageParam, false);
                                        break;
                                    case 'LOADMORE':
                                        this.page++;
                                        for (this.page; this.page <= resolve.payload; this.page++) {
                                            this.fetchData(false, null, 0, false);
                                        }
                                        this.page--;
                                        break;
                                    default:
                                        break;
                                }
                            }
                        })
                        .catch(error => console.log(error))
                        // Set loading prop to false when done.
                        .finally(() => this.loading = false);
                },
                // Lazy loading data chunks handler.
                loadMore() {
                    this.page++;
                    this.fetchData();
                },
                // Data alteration function for reloading data with set filters.
                toggleOption(facetAlias, optionAlias) {
                    let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
                    if (changedFacet.values.includes(optionAlias)) {
                        changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
                    } else {
                        changedFacet.values.push(optionAlias);
                    }
                    this.fetchData(true);
                },
                // Data alteration function for resetting all active filters and reloading data.
                reset() {
                    this.data.facets.forEach(facet => facet.values = []);
                    this.searchText = '';
                    this.fetchData(true);
                },
                // Function for updating browser URL and state history with changed filters, search input or page.
                updateUrl() {
                    if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                        history.pushState({}, null, window.location.pathname);
                        return;
                    }
                    const urlParams = new URLSearchParams();
                    if (this.queryModel.Text) urlParams.append('text', this.queryModel.Text);
                    if (this.queryModel.FilterItems.length) {
                        this.queryModel.FilterItems.forEach(filterItem => urlParams.append(filterItem.Key, filterItem.Values.join()))
                    }
                    if (this.page > 1) urlParams.append('page', this.page);
                    history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
                },

                // Check if a new month should be shown in the template and return the display value if so.
                showMonth(index, date) {
                    let returnVal = null;
                    if (index === 0) {
                        returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                    else {
                        let prevEvent = this.data.items[index - 1];
                        if (prevEvent) {
                            if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.publicationDate).getMonth() || this.toDateObj(date).getYear() !== this.toDateObj(prevEvent.publicationDate).getYear()) {
                                returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                            }
                        }
                    }
                    return returnVal;
                },

                // Simple capitalize function.
                capitalize(value) {
                    if (!value) return '';
                    value = value.toString();
                    return value.charAt(0).toUpperCase() + value.slice(1);
                },
                // Simple whitespace strip function.
                strip(value) {
                    if (!value) return '';
                    value = value.toString();
                    return value.replace(/\s/g, '');
                },
                // Simple date object handler.
                toDateObj(dateString) {
                    return new Date(dateString);
                },
                // Simple date string handler.
                toDateStr(dateString) {
                    return new Date(dateString).toLocaleDateString('nl-NL');
                },
                toMediaQuery(mediaString) {
                    if (!mediaString) return '';
                    return mediaString.split('"')[1];
                },
                reverse(array) {
                    return [...array].reverse();
                }
            },
            created() {
                if (getCookie('ListBlockView') !== "") {
                    this.blockView = false;
                }
                // Data alteration function for reloading data with textual search input.
                this.changeSearchText = debounce(event => {
                    this.fetchData(true);
                });
            },
            mounted() {
                // Set data props according to data attributes in HTML template.
                if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api;
                if (this.$el.dataset.parentid) this.parentId = this.$el.dataset.parentid;
                if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
                if (this.$el.dataset.sortkey) this.dataSortKey = this.$el.dataset.sortkey;
                if (this.$el.dataset.sortdirection) this.dataSortDirection = this.$el.dataset.sortdirection;
                if (this.$el.dataset.showxamountoftotalitems) this.dataAmountsSentence = this.$el.dataset.showxamountoftotalitems;

                // Construct query model and alter data props based on URL search params.
                const urlParams = new URLSearchParams(window.location.search);

                let pageParam = urlParams.has('page') ? parseInt(urlParams.get('page')) : 0;
                if (pageParam) urlParams.delete('page');
                if (pageParam < 2) pageParam = 0;

                this.searchText = urlParams.has('text') ? urlParams.get('text') : '';
                if (this.searchText) urlParams.delete('text');

                let filterParams = urlParams.toString() ? urlParams : null;

                // Initial data fetch.
                this.fetchData(true, filterParams, pageParam);
            },
        });

        // Mount the app on the targeted element.
        listViewApp.mount('#list-view');
    }
});